import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { LanguageProvider } from "@/contexts/LanguageContext";
import LanguageRouter from "@/components/LanguageRouter";

// Lazy load components for code splitting
const OfflineIndicator = lazy(() => import("@/components/OfflineIndicator"));
const CookieConsent = lazy(() => import("@/components/CookieConsent"));
const Index = lazy(() => import("./pages/Index"));
const Blog = lazy(() => import("./pages/Blog"));
const Cases = lazy(() => import("./pages/Cases"));
const Auth = lazy(() => import("./pages/Auth"));
const Admin = lazy(() => import("./pages/Admin"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Minimal loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const queryClient = new QueryClient();

// Wrapper component for routes that need language detection
const LocalizedRoutes = () => (
  <LanguageRouter>
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Portuguese (default) - no prefix */}
        <Route path="/" element={<Index />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/cases" element={<Cases />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/admin" element={<Admin />} />
        
        {/* English routes */}
        <Route path="/en" element={<Index />} />
        <Route path="/en/blog" element={<Blog />} />
        <Route path="/en/cases" element={<Cases />} />
        <Route path="/en/auth" element={<Auth />} />
        <Route path="/en/admin" element={<Admin />} />
        
        {/* Spanish routes */}
        <Route path="/es" element={<Index />} />
        <Route path="/es/blog" element={<Blog />} />
        <Route path="/es/cases" element={<Cases />} />
        <Route path="/es/auth" element={<Auth />} />
        <Route path="/es/admin" element={<Admin />} />
        
        {/* French routes */}
        <Route path="/fr" element={<Index />} />
        <Route path="/fr/blog" element={<Blog />} />
        <Route path="/fr/cases" element={<Cases />} />
        <Route path="/fr/auth" element={<Auth />} />
        <Route path="/fr/admin" element={<Admin />} />
        
        {/* Chinese routes */}
        <Route path="/zh" element={<Index />} />
        <Route path="/zh/blog" element={<Blog />} />
        <Route path="/zh/cases" element={<Cases />} />
        <Route path="/zh/auth" element={<Auth />} />
        <Route path="/zh/admin" element={<Admin />} />
        
        {/* Catch-all for 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  </LanguageRouter>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
      <LanguageProvider>
        <TooltipProvider>
          <Suspense fallback={null}>
            <OfflineIndicator />
            <CookieConsent />
          </Suspense>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <LocalizedRoutes />
          </BrowserRouter>
        </TooltipProvider>
      </LanguageProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
